import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "./client";
import { refusalFrom } from "./refusal";

export const AUDIT_PAGE_SIZE = 100;

export interface AuditEvent {
  id: string;
  created_at: string;
  event_type: string;
  actor_id: string | null;
  reason: string | null;
  /** `security` (a sign-in, second-factor or role event) or `setup` (a configuration act read from the
   * ledger, G-P8-28). Absent means `security`: a response from before the field existed. */
  source?: string;
  /** For a `setup` act: the kind of thing and its identifier, as the ledger records them. */
  object_type?: string | null;
  object_id?: string | null;
}

export interface AuditHistory {
  events: AuditEvent[];
  has_more: boolean;
  attribution_note: string;
}

async function failure(response: Response, message: string): Promise<never> {
  const body: unknown = await response.json().catch(() => null);
  throw refusalFrom(response, body, message);
}

function isAuditEvent(value: unknown): value is AuditEvent {
  return value !== null && typeof value === "object"
    && "id" in value && typeof value.id === "string"
    && "created_at" in value && typeof value.created_at === "string"
    && !Number.isNaN(Date.parse(value.created_at))
    && "event_type" in value && typeof value.event_type === "string"
    && "actor_id" in value && (value.actor_id === null || typeof value.actor_id === "string")
    && "reason" in value && (value.reason === null || typeof value.reason === "string")
    && (!("source" in value) || typeof value.source === "string")
    && (!("object_type" in value) || value.object_type === null || typeof value.object_type === "string")
    && (!("object_id" in value) || value.object_id === null || typeof value.object_id === "string");
}

function historyPage(value: unknown): AuditHistory {
  if (value === null || typeof value !== "object"
    || !("events" in value) || !Array.isArray(value.events)
    || value.events.length > AUDIT_PAGE_SIZE || !value.events.every(isAuditEvent)
    || !("has_more" in value) || typeof value.has_more !== "boolean"
    || !("attribution_note" in value) || typeof value.attribution_note !== "string") {
    throw new Error("The security history response could not be read.");
  }
  return { events: value.events, has_more: value.has_more, attribution_note: value.attribution_note };
}

export function useAuditHistory(enabled: boolean, offset: number) {
  return useQuery({
    queryKey: ["audit", "history", offset],
    enabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    queryFn: async (): Promise<AuditHistory> => {
      const response = await apiFetch(`/api/v1/audit/history?limit=${AUDIT_PAGE_SIZE}&offset=${offset}`);
      if (!response.ok) return failure(response, "Security history could not be read.");
      return historyPage(await response.json());
    },
  });
}

export function useAuditExport() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (): Promise<Blob> => {
      const response = await apiFetch("/api/v1/audit/export", { method: "POST" });
      if (!response.ok) return failure(response, "The security export could not be generated.");
      if (response.headers.get("content-type")?.split(";")[0]?.trim().toLowerCase() !== "text/csv") {
        throw new Error("The security export response was not a CSV file.");
      }
      return response.blob();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["audit", "history"] }),
  });
}

export function saveAuditExport(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "workspace-security.csv";
  try {
    anchor.click();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
